import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Switch, FormControlLabel,
  TextField, Button, Divider, Stack, Alert, Snackbar, Chip, Tooltip,
  CircularProgress, Table, TableHead, TableRow, TableCell,
  TableBody, SvgIcon,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import TelegramIcon from '@mui/icons-material/Telegram';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import CampaignIcon from '@mui/icons-material/Campaign';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { alpha, useTheme } from '@mui/material/styles';
import apiService from '../../services/apiServices';
import useConfirm from '../../hooks/useConfirm';

const SlackIcon = (props) => (
  <SvgIcon viewBox="0 0 122 122" {...props}>
    <path fill="#E01E5A" d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" />
    <path fill="#36C5F0" d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" />
    <path fill="#2EB67D" d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" />
    <path fill="#ECB22E" d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" />
  </SvgIcon>
);

const EVENT_LABELS = {
  SALARY_CHANGED:            'Paga e ndryshuar',
  SCHEDULE_CHANGED:          'Orari i ndryshuar',
  REQUEST_CREATED:           'Kërkesë e re nga punëtori',
  LEAVE_APPROVED:            'Pushimi u aprovua',
  LEAVE_PENDING_SUPERVISOR:  'Pushim — pret supervizorin',
  LEAVE_PENDING_HR:          'Pushim — pret HR',
  LEAVE_REJECTED_SUPERVISOR: 'Pushim — refuzuar nga supervizori',
  LEAVE_REJECTED_HR:         'Pushim — refuzuar nga HR',
  CUSTOM:                    'Njoftim me tekst të lirë',
};

const CHANNELS = [
  { id: 'email',    label: 'Email',    Icon: EmailIcon,    color: (t) => t.palette.success.main },
  { id: 'slack',    label: 'Slack',    Icon: SlackIcon,    color: () => '#4A154B' },
  { id: 'telegram', label: 'Telegram', Icon: TelegramIcon, color: () => '#229ED9' },
];

function ChannelStatusChip({ enabled, status }) {
  if (!enabled) return null;
  if (!status) return <Chip label="Po kontrollohet…" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
  if (status.ok) {
    return (
      <Chip
        icon={<LinkIcon sx={{ fontSize: 12 }} />}
        label="I lidhur"
        size="small"
        color="success"
        variant="outlined"
        sx={{ height: 20, fontSize: '0.7rem' }}
      />
    );
  }
  return (
    <Chip
      icon={<LinkOffIcon sx={{ fontSize: 12 }} />}
      label="Pa lidhje"
      size="small"
      color="error"
      variant="outlined"
      sx={{ height: 20, fontSize: '0.7rem' }}
    />
  );
}

function ConnectionLines({ status }) {
  const lines = [];
  if (!status) {
    lines.push({ label: 'Lidhja', value: 'po verifikohet…' });
  } else if (status.disabled) {
    lines.push({ label: 'Lidhja', value: 'çaktivizuar' });
  } else if (!status.ok) {
    lines.push({ label: 'Lidhja', value: status.error || 'gabim' });
  } else {
    if (status.from || status.host) {
      const parts = [status.fromName ? `"${status.fromName}"` : null, status.from, status.host && `via ${status.host}`].filter(Boolean);
      lines.push({ label: 'Dërgon nga', value: parts.join(' · ') });
      if (status.user && status.user !== status.from) lines.push({ label: 'SMTP user', value: status.user });
    }
    if (status.team || status.botUser) {
      lines.push({ label: 'Workspace', value: status.team || '—' });
      if (status.botUser) lines.push({ label: 'Bot user', value: `@${status.botUser}` });
      if (status.defaultChannel) lines.push({ label: 'Kanal default', value: status.defaultChannel });
    }
    if (status.via === 'webhook') {
      lines.push({ label: 'Tipi', value: 'Incoming Webhook' });
      if (status.defaultChannel) lines.push({ label: 'Kanal default', value: status.defaultChannel });
    }
    if (status.botUsername) {
      lines.push({ label: 'Bot', value: `@${status.botUsername}${status.botName ? ` (${status.botName})` : ''}` });
    }
  }
  return (
    <Stack spacing={0.25}>
      {lines.map((l, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96 }}>{l.label}:</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{l.value}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

// ── Slack Worker Audit — per-worker DM rollout status ───────────────────────
function SlackWorkerAudit({ onSnack }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [busyBulk, setBusyBulk] = useState(false);
  const [busyRow, setBusyRow]   = useState(null);  // workerId currently being invited

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiService().get('notifications/slack/audit');
      if (r.success) setData(r.data);
      else { setError(r.error || 'Gabim'); }
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inviteOne = async (workerId, name) => {
    setBusyRow(workerId);
    try {
      const r = await apiService().post(`notifications/slack/invite-worker/${workerId}`);
      if (r.success) onSnack?.({ open: true, severity: 'success', msg: `Invite u dërgua te ${name}` });
      else           onSnack?.({ open: true, severity: 'error', msg: r.error || 'Gabim' });
    } catch (e) { onSnack?.({ open: true, severity: 'error', msg: e.message }); }
    setBusyRow(null);
  };

  const [confirm, confirmUI] = useConfirm();

  const inviteAllUnbound = async () => {
    // Veprim masiv: konfirmimi duhet ta thotë SASINË, jo vetëm "Vazhdo?".
    if (!(await confirm({
      title: 'Dërgo ftesa Slack',
      message: 'Çdo punëtor pa llogari në workspace merr një email me invite link.',
      details: `${data?.counts?.not_in_workspace || 0} punëtorë`,
      confirmLabel: 'Dërgo',
      color: 'primary',
    }))) return;
    setBusyBulk(true);
    try {
      const r = await apiService().post('notifications/slack/invite-unbound');
      if (r.success) {
        onSnack?.({ open: true, severity: 'success',
          msg: `Invite u dërgua te ${r.data.sent}/${r.data.total} punëtorë.${r.data.failed > 0 ? ` ${r.data.failed} dështuan.` : ''}` });
      } else {
        onSnack?.({ open: true, severity: 'error', msg: r.error || 'Gabim' });
      }
    } catch (e) { onSnack?.({ open: true, severity: 'error', msg: e.message }); }
    setBusyBulk(false);
  };

  useEffect(() => { load(); }, []);

  const copyEmails = (filter) => {
    if (!data?.rows) return;
    const emails = data.rows
      .filter((r) => filter(r))
      .map((r) => r.email)
      .filter(Boolean);
    if (!emails.length) {
      onSnack?.({ open: true, severity: 'info', msg: 'Asnjë email për të kopjuar.' });
      return;
    }
    navigator.clipboard.writeText(emails.join(', '));
    onSnack?.({ open: true, severity: 'success', msg: `${emails.length} email u kopjuan në clipboard.` });
  };

  const counts = data?.counts || {};
  const total  = data?.total  || 0;
  const bound  = counts.bound || 0;
  const missing = counts.not_in_workspace || 0;
  const noEmail = counts.no_email || 0;

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }} elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <GroupsIcon sx={{ fontSize: 20, color: '#4A154B' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          Slack Sync — punëtorët
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
          onClick={load}
          disabled={loading}
        >
          Rifresko
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Që çdo punëtor të marrë njoftime në Slack si DM, duhet të jetë anëtar i workspace-it me email-in
        që ka në Nexuma. Kjo tabelë tregon statusin për të gjithë punëtorët aktivë.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {/* Summary cards */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'success.light', bgcolor: alpha('#4caf50', 0.05), minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Të lidhur</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>{bound} / {total}</Typography>
        </Box>
        <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'error.light', bgcolor: alpha('#f44336', 0.05), minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Pa Slack</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>{missing}</Typography>
        </Box>
        {noEmail > 0 && (
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'warning.light', bgcolor: alpha('#ff9800', 0.05), minWidth: 100 }}>
            <Typography variant="caption" color="text.secondary">Pa email</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>{noEmail}</Typography>
          </Box>
        )}
      </Stack>

      {/* Bulk actions */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Tooltip title="Dërgon email automatikisht te ҫdo punëtor pa Slack me link për t'u bashkuar. Kërkon SLACK_INVITE_LINK në .env.">
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={busyBulk ? <CircularProgress size={12} color="inherit" /> : <SendIcon sx={{ fontSize: 14 }} />}
              onClick={inviteAllUnbound}
              disabled={busyBulk || loading || missing === 0}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, textTransform: 'none' }}
            >
              {busyBulk ? 'Po dërgon…' : `Fto ${missing} punëtorë (email + Slack)`}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Kopjon në clipboard email-et e punëtorëve që s'janë në workspace. Ngjit te Slack → Invite teammates → Enter.">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              onClick={() => copyEmails((r) => r.status === 'not_in_workspace')}
              disabled={loading || missing === 0}
              sx={{ textTransform: 'none' }}
            >
              Kopjo {missing} email
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* Detail table */}
      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : data?.rows?.length > 0 ? (
        <Box sx={{ maxHeight: 360, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', width: 36 }} />
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Punëtori</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Statusi</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Slack ID</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', width: 120 }}>Veprim</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((r) => {
                const isBound = r.status === 'bound';
                const isMissing = r.status === 'not_in_workspace';
                const isNoEmail = r.status === 'no_email';
                return (
                  <TableRow key={r.workerId} hover>
                    {/* Icons, not emoji — emoji render differently per platform,
                        are announced as their unicode name by screen readers,
                        and can't inherit the status colour. */}
                    <TableCell sx={{ textAlign: 'center', lineHeight: 0 }}>
                      {isBound
                        ? <CheckCircleRoundedIcon aria-hidden sx={{ fontSize: 18, color: 'success.main' }} />
                        : isMissing
                        ? <CancelRoundedIcon aria-hidden sx={{ fontSize: 18, color: 'error.main' }} />
                        : <ErrorOutlineRoundedIcon aria-hidden sx={{ fontSize: 18, color: 'warning.main' }} />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.email || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={isBound ? 'I lidhur' : isMissing ? 'S\'është në Slack' : isNoEmail ? 'Pa email' : r.status}
                        size="small"
                        color={isBound ? 'success' : isMissing ? 'error' : 'warning'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      {r.slackUserId ? (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          {r.slackUserId}{r.slackUsername && ` · ${r.slackUsername}`}
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {!isBound && r.email && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={busyRow === r.workerId ? <CircularProgress size={10} /> : <SendIcon sx={{ fontSize: 12 }} />}
                          onClick={() => inviteOne(r.workerId, r.name)}
                          disabled={busyRow === r.workerId}
                          sx={{ fontSize: 10, py: 0.25, textTransform: 'none' }}
                        >
                          {busyRow === r.workerId ? 'Po dërgon…' : 'Fto'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        💡 Pas ftesës: hap Slack → klik <strong>"Invite teammates"</strong> në fund të sidebar-it → ngjit email-et e kopjuara →
        Send. Pasi punëtori pranon ftesën, klik <strong>Rifresko</strong> këtu — sistemi do ta lidhë automatikisht dhe
        DM-të do shkojnë drejt e te ai/ajo në Slack.
      </Typography>
      {confirmUI}
    </Paper>
  );
}

export default function NotificationSettings() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [settings, setSettings] = useState(null);
  const [knownEvents, setKnownEvents] = useState([]);
  const [testTarget, setTestTarget] = useState({ email: '', slack: '', telegram: '' });
  const [connStatus, setConnStatus] = useState(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [platformSetup, setPlatformSetup] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiService().get('notifications/settings');
      if (r.success) {
        setSettings(r.data.settings || {});
        setKnownEvents(r.data.knownEvents || []);
      } else {
        setSnack({ open: true, msg: r.error || 'Nuk u ngarkuan settingat', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    setRefreshingStatus(true);
    try {
      const r = await apiService().get('notifications/connection-status');
      if (r.success) setConnStatus(r.data.status || {});
    } catch (_e) { /* non-fatal */ }
    finally { setRefreshingStatus(false); }
  };

  const loadPlatformSetup = async () => {
    try {
      const r = await apiService().get('notifications/platform-setup');
      if (r.success) setPlatformSetup(r.data);
    } catch (_e) { /* non-fatal */ }
  };

  useEffect(() => { load(); refreshStatus(); loadPlatformSetup(); }, []);

  const routedByChannel = useMemo(() => {
    const out = { email: [], slack: [], telegram: [] };
    const routing = settings?.event_routing || {};
    for (const ev of knownEvents) {
      const rules = routing[ev] || {};
      for (const ch of ['email', 'slack', 'telegram']) {
        if (rules[ch] && settings?.[`${ch}_enabled`]) out[ch].push(ev);
      }
    }
    return out;
  }, [settings, knownEvents]);

  const update = (patch) => setSettings((s) => ({ ...(s || {}), ...patch }));
  const updateRouting = (event, channel, on) => {
    const next = { ...(settings?.event_routing || {}) };
    next[event] = { ...(next[event] || {}), [channel]: on };
    update({ event_routing: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        email_enabled:    !!settings.email_enabled,
        slack_enabled:    !!settings.slack_enabled,
        telegram_enabled: !!settings.telegram_enabled,
        event_routing:    settings.event_routing || {},
        admin_email:      settings.admin_email || null,
      };
      const r = await apiService().put('notifications/settings', payload);
      if (r.success) {
        setSettings(r.data.settings || settings);
        setSnack({ open: true, msg: 'Konfigurimi u ruajt', severity: 'success' });
        refreshStatus();
      } else {
        setSnack({ open: true, msg: r.error || 'Nuk u ruajt', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (channel) => {
    setTesting(channel);
    try {
      const r = await apiService().post('notifications/test', {
        channel,
        target: testTarget[channel] || undefined,
      });
      const result = r?.data?.results?.[0];
      if (r.success && result?.ok) {
        setSnack({ open: true, msg: `Test ${channel} u dërgua`, severity: 'success' });
      } else {
        setSnack({ open: true, msg: result?.error || r?.error || `Test ${channel} dështoi`, severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: 'error' });
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }
  if (!settings) {
    return <Alert severity="error">Nuk u ngarkuan konfigurimet e njoftimeve.</Alert>;
  }

  const platformReady = platformSetup
    && platformSetup.email.configured
    && platformSetup.slack.configured
    && platformSetup.telegram.configured;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Njoftimet</Typography>
          <Typography variant="body2" color="text.secondary">
            Aktivizo kanalet që dëshiron. Eventet rrjedhin <strong>automatikisht</strong> kur ndodhin
            (paga, orari, pushime, kërkesa). Kredencialet menaxhohen nga platforma — pa konfigurime nga ana jote.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Verifiko lidhjet">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={refreshingStatus ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={refreshStatus}
                disabled={refreshingStatus}
              >
                Verifiko
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={save}
            disabled={saving}
          >
            Ruaj
          </Button>
        </Stack>
      </Box>

      {platformSetup && (
        <Alert
          severity={platformReady ? 'success' : 'info'}
          sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {platformReady ? 'Nexuma është gati — pa konfigurime nga ti' : 'Statusi i kanaleve'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Chip
              size="small"
              icon={platformSetup.email.configured ? <LinkIcon sx={{ fontSize: 12 }} /> : <LinkOffIcon sx={{ fontSize: 12 }} />}
              label={`Email: ${platformSetup.email.configured ? `gati${platformSetup.email.from ? ` (${platformSetup.email.from})` : ''}` : 'pa konfiguruar'}`}
              color={platformSetup.email.configured ? 'success' : 'default'}
              variant="outlined"
              sx={{ height: 22 }}
            />
            <Chip
              size="small"
              icon={platformSetup.slack.configured ? <LinkIcon sx={{ fontSize: 12 }} /> : <LinkOffIcon sx={{ fontSize: 12 }} />}
              label={`Slack: ${platformSetup.slack.configured ? `gati (${platformSetup.slack.via})` : 'pa konfiguruar'}`}
              color={platformSetup.slack.configured ? 'success' : 'default'}
              variant="outlined"
              sx={{ height: 22 }}
            />
            <Chip
              size="small"
              icon={platformSetup.telegram.configured ? <LinkIcon sx={{ fontSize: 12 }} /> : <LinkOffIcon sx={{ fontSize: 12 }} />}
              label={`Telegram: ${platformSetup.telegram.configured ? `gati (${platformSetup.telegram.botUsername || '@bot'})` : 'pa konfiguruar'}`}
              color={platformSetup.telegram.configured ? 'success' : 'default'}
              variant="outlined"
              sx={{ height: 22 }}
            />
          </Stack>
          {!platformReady && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Kanalet me etiketë <em>"pa konfiguruar"</em> kërkojnë që operatori të vendosë variablat përkatëse në <code>.env</code> (<code>SMTP_*</code>, <code>SLACK_*</code>, <code>TELEGRAM_*</code>).
            </Typography>
          )}
        </Alert>
      )}

      {/* Grid me 2 kolona — organizim profesional (si karta krah-për-krah) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'start', mb: 2 }}>

      {/* ── Kolona majtas: Kanalet ── */}
      <Stack spacing={2}>
      {CHANNELS.map(({ id, label, Icon, color: colorFn }) => {
        const color = colorFn(theme);
        const enabled = !!settings[`${id}_enabled`];
        const status  = connStatus?.[id];
        const events  = routedByChannel[id];

        return (
          <Paper
            key={id}
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha(color, 0.25),
              bgcolor: alpha(color, 0.03),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon sx={{ fontSize: 18, color }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{label}</Typography>
                {enabled && <Chip label="Aktiv" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                <ChannelStatusChip enabled={enabled} status={status} />
              </Box>
              <FormControlLabel
                control={<Switch checked={enabled} onChange={(e) => update({ [`${id}_enabled`]: e.target.checked })} />}
                label=""
                sx={{ m: 0 }}
              />
            </Box>

            {enabled && (
              <>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: alpha(color, 0.05),
                    border: '1px dashed',
                    borderColor: alpha(color, 0.25),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                    <LinkIcon sx={{ fontSize: 14, color }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color }}>Lidhet me</Typography>
                  </Box>
                  <ConnectionLines status={status} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.25, mb: 0.75 }}>
                    <CampaignIcon sx={{ fontSize: 14, color }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color }}>Çfarë komunikon</Typography>
                  </Box>
                  {events.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Asnjë event i caktuar — kontrollo matricën më poshtë.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {events.map((ev) => (
                        <Chip
                          key={ev}
                          label={EVENT_LABELS[ev] || ev}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: alpha(color, 0.12),
                            color,
                            fontWeight: 600,
                            border: '1px solid',
                            borderColor: alpha(color, 0.3),
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                  <TextField
                    size="small"
                    placeholder={
                      id === 'email'    ? 'Test recipient (email) — opsionale'
                    : id === 'slack'    ? 'Test channel (#njoftime ose @user) — opsionale'
                    :                     'Test chat_id (numerik) — opsionale'
                    }
                    value={testTarget[id]}
                    onChange={(e) => setTestTarget((t) => ({ ...t, [id]: e.target.value }))}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={testing === id ? <CircularProgress size={14} /> : <SendIcon />}
                    onClick={() => sendTest(id)}
                    disabled={testing === id || !(status?.ok)}
                  >
                    Dërgo test
                  </Button>
                </Stack>
              </>
            )}
          </Paper>
        );
      })}
      </Stack>

      {/* ── Kolona djathtas: çfarë dërgohet ku + email administrativ ── */}
      <Stack spacing={2}>

      {/* Event routing matrix */}
      <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <VerifiedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Cilët evente dërgohen ku</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Cakto për secilin event në cilët kanale të shkojë. Kanalet që nuk janë aktiv më lart, injorohen automatikisht.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700 }}>Eventi</TableCell>
              {CHANNELS.map((c) => (
                <TableCell key={c.id} align="center" sx={{ fontWeight: 700, width: 90 }}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {knownEvents.map((ev) => (
              <TableRow key={ev} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{EVENT_LABELS[ev] || ev}</Typography>
                  <Typography variant="caption" color="text.secondary">{ev}</Typography>
                </TableCell>
                {CHANNELS.map((c) => (
                  <TableCell key={c.id} align="center">
                    <Switch
                      size="small"
                      checked={!!(settings.event_routing?.[ev]?.[c.id])}
                      onChange={(e) => updateRouting(ev, c.id, e.target.checked)}
                      disabled={!settings[`${c.id}_enabled`]}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Paper>

      <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Email administrativ</Typography>
        <TextField
          size="small"
          fullWidth
          label="Admin recipient"
          value={settings.admin_email || ''}
          onChange={(e) => update({ admin_email: e.target.value })}
          placeholder="admin@kompania.com"
          helperText="Adresa ku shkojnë njoftimet kur nuk është caktuar punëtor specifik."
        />
      </Paper>

      </Stack>
      </Box>
      {/* fund i grid-it me 2 kolona */}

      {/* Slack worker audit — full width poshtë (tabelë e gjerë) */}
      {platformSetup?.slack?.configured && settings.slack_enabled && (
        <SlackWorkerAudit onSnack={setSnack} />
      )}


      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
